import {
  Box,
  ClickAwayListener,
  makeStyles,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import {
  ConditionalRender,
  CustomizedTypography,
  Stack,
  VStack
} from '@components/Customized';
import BottomNav from '@routes/Common/BottomNav';
import CommonLayout from '@routes/Common/CommonLayout';
import MobileHeader from './component/MobileHeader';
import Container from './component/Container';
import { ManufactureHistoryList } from './component/ManufactureHistoryList';
import { AmountCard } from './component/AmountCard';
import {
  ProjectPurchaseOrderManageStatusType,
  useFetchMyPaymentDataByQuotationUserLazyQuery
} from '@generated/graphql';
import React, { useEffect, useRef, useState } from 'react';
import { Pagination, Skeleton } from '@material-ui/lab';
import { SearchInput } from '@components/SearchInput';
import { projectPurchaseOrderManageStatusEnumToString } from '@utils/enumToString';
import useQueryString from '@hooks/useQueryString';
import { Information } from '@carbon/icons-react';
import { colorSet } from '@components/Provider';

const PAYMENT_LIMIT = 10;

const Payment: React.FunctionComponent = () => {
  const { paymentConatiner } = useStyles();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const { query, onUpdateQueryString, onMoveQueryString } = useQueryString<{
    searchKeyword?: string;
    searchType?: string;
    page?: string;
  }>();
  const listTopRef = useRef<HTMLDivElement | null>(null);

  const [
    fetchMyPaymentData,
    { data, loading: myPaymentDataLoading }
  ] = useFetchMyPaymentDataByQuotationUserLazyQuery({
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    const { page, searchKeyword, searchType } = query;

    fetchMyPaymentData({
      variables: {
        input: {
          searchKeyword,
          limit: PAYMENT_LIMIT,
          offset: page ? (Number(page ?? 0) - 1) * PAYMENT_LIMIT : 0,
          searchType: searchType as ProjectPurchaseOrderManageStatusType
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMyPaymentData, query.page, query.searchKeyword, query.searchType]);

  const estimatedAmount =
    data?.fetchMyPaymentDataByQuotationUser.data?.estimatedAmount ?? 0;
  const completionAmount =
    data?.fetchMyPaymentDataByQuotationUser.data?.completionAmount ?? 0;
  const capaPoint =
    data?.fetchMyPaymentDataByQuotationUser.data?.point?.toLocaleString('kr') ??
    '0';
  const list =
    data?.fetchMyPaymentDataByQuotationUser.data?.purchaseOrderInfoList ?? [];
  const totalCount =
    data?.fetchMyPaymentDataByQuotationUser.data?.totalCount ?? 0;

  return (
    <CommonLayout bottomNav={<BottomNav />}>
      <MobileHeader visible={matches} />

      <Container>
        <div className={paymentConatiner}>
          <div className="payment-management-area">
            <Stack
              justifyContent="space-between"
              alignItems="center"
              margin="0 0 20px 0"
            >
              <CustomizedTypography variant="h3">발주관리</CustomizedTypography>

              <Stack alignItems="center" gap={4}>
                <CapaPointInformation capaPoint={capaPoint} />
              </Stack>
            </Stack>
            <div className="card-container">
              <Box width="100%">
                <AmountCard
                  title={'구매확정 예정 금액'}
                  description={
                    '고객님이 캐파 안전결제를 통해 발주한 총 결제 대금입니다. \n파트너로부터 제조 작업물을 배송 받고 구매확정을 해야하는 총 금액입니다.'
                  }
                  amount={estimatedAmount}
                />
              </Box>
              <Box width="100%">
                <AmountCard
                  title={'정산완료 금액'}
                  description={
                    '파트너에게 구매확정한 총 결제 대금입니다. \n영업일 기준 최소 3영업일, 최대 5영업일 내에 파트너의 정산 계좌로결제 대금이 정산됩니다.'
                  }
                  amount={completionAmount}
                  colorType="blue"
                />
              </Box>
            </div>
          </div>
          <div className="history-area" ref={listTopRef}>
            <CustomizedTypography
              variant="subtitle1"
              fontWeight="500"
              gutterBottoms={16}
            >
              발주 내역
            </CustomizedTypography>
            <Box width={matches ? '100%' : 375}>
              <SearchInput
                options={Object.values(
                  ProjectPurchaseOrderManageStatusType
                ).map((value) => ({
                  value,
                  label: projectPurchaseOrderManageStatusEnumToString(value)
                }))}
                onSubmit={({ keyword, tag }) => {
                  onMoveQueryString({
                    searchKeyword: keyword,
                    searchType: tag
                  });
                }}
              />
            </Box>

            <ConditionalRender
              visible={!myPaymentDataLoading}
              fallback={<FallbackCards />}
            >
              <ManufactureHistoryList list={list} />
            </ConditionalRender>
            {!!totalCount && (
              <Box marginTop={2} display="flex" justifyContent="center">
                <Pagination
                  page={query.page ? Number(query.page) : 1}
                  count={Math.ceil(totalCount / PAYMENT_LIMIT)}
                  onChange={(_, page) => {
                    listTopRef.current?.scrollIntoView({ block: 'start' });

                    onUpdateQueryString({
                      page: page.toString()
                    });
                  }}
                />
              </Box>
            )}
          </div>
        </div>
      </Container>
    </CommonLayout>
  );
};

export default Payment;

const useStyles = makeStyles((theme) => ({
  paymentConatiner: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    '& .payment-management-area': {
      width: '100%',
      marginBottom: 36,
      '& .card-container': {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        gap: 16,
        [theme.breakpoints.down('md')]: {
          flexDirection: 'column',
          gap: 12
        }
      }
    },
    '& .history-area': {
      width: '100%'
    }
  }
}));

const FallbackCards = React.memo(() => {
  return (
    <VStack gap={16}>
      {[1, 2, 3, 4, 5].map((_, index) => (
        <FallbackCard key={index} />
      ))}
    </VStack>
  );
});

export const FallbackCard = React.memo(() => {
  return (
    <Skeleton
      width="100%"
      height={208}
      variant="rect"
      style={{
        borderRadius: 12
      }}
    />
  );
});

const CapaPointInformation = ({ capaPoint }: { capaPoint: string }) => {
  const [open, setOpen] = useState(false);
  const matches = useMediaQuery(useTheme().breakpoints.down('md'));

  return (
    <>
      <CustomizedTypography variant="body2" fontWeight="500">
        캐파 포인트: {capaPoint}
      </CustomizedTypography>
      {matches ? (
        <ClickAwayListener
          onClickAway={() => {
            setOpen(false);
          }}
        >
          <Tooltip
            open={open}
            PopperProps={{
              disablePortal: true
            }}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={
              <>
                <CustomizedTypography
                  variant="body2"
                  style={{
                    color: colorSet.gray000,
                    wordBreak: 'break-all'
                  }}
                >
                  캐파 내에서 구매확정을 통한 거래완료 시, 거래액의 0.1% 가
                  적립됩니다.
                </CustomizedTypography>
                <CustomizedTypography
                  variant="subtitle2"
                  style={{
                    color: colorSet.gray000
                  }}
                >
                  (현재는 적립만 가능합니다. 추후 포인트를 결제 등에 사용될 수
                  있도록 개발 예정입니다.)
                </CustomizedTypography>
              </>
            }
            placement="bottom-start"
          >
            <Information
              onTouchStart={() => {
                setOpen(true);
              }}
              style={{
                marginTop: 2
              }}
              size={14}
            />
          </Tooltip>
        </ClickAwayListener>
      ) : (
        <Tooltip
          title={
            <>
              <CustomizedTypography
                variant="body2"
                style={{
                  color: colorSet.gray000,
                  wordBreak: 'break-all'
                }}
              >
                캐파 내에서 구매확정을 통한 거래완료 시, 거래액의 0.1% 가
                적립됩니다.
              </CustomizedTypography>
              <CustomizedTypography
                variant="subtitle2"
                style={{
                  color: colorSet.gray000
                }}
              >
                (현재는 적립만 가능합니다. 추후 포인트를 결제 등에 사용될 수
                있도록 개발 예정입니다.)
              </CustomizedTypography>
            </>
          }
          placement="bottom-start"
        >
          <Information
            style={{
              marginTop: 2
            }}
            size={14}
          />
        </Tooltip>
      )}
    </>
  );
};
